import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * The useControllable hook is designed to manage controlled and uncontrolled
 * states in React components.
 *
 * **Notes**:
 * - If `controlledValue` is provided, the component is considered controlled.
 * - If the component transitions from uncontrolled to controlled or vice versa, a warning is logged to the console.
 * @param controlledValue The controlled value that can be passed to the component.
 * @param onChange A callback function triggered when the value changes.
 * @param defaultValue The default value when the component is uncontrolled.
 * @returns [value, onChange]
 *
 * @example
 * ```
 * const YourComponent = ({ controlledValue, onChange, defaultValue }) => {
 * const [value, setValue] = useControllable(controlledValue, onChange, defaultValue);
 *
 *   // ... rest of your component logic using the 'value'
 *
 *   return (
 *     // ... your component JSX
 *   );
};
 * ```
 */
export function useControllable<T>(
  controlledValue: T | undefined,
  onChange?: (value: T) => void,
  defaultValue?: T
) {
  const [internalValue, setInternalValue] = useState(defaultValue);

  const isControlled = controlledValue !== undefined;
  const wasControlled = useRef(isControlled);
  const didWarnOnUncontrolledToControlled = useRef(false);
  const didWarnOnControlledToUncontrolled = useRef(false);

  if (
    isControlled &&
    !wasControlled.current &&
    !didWarnOnUncontrolledToControlled.current
  ) {
    didWarnOnUncontrolledToControlled.current = true;
    wasControlled.current = isControlled;
    console.error(
      'A component is changing from uncontrolled to controlled. This may be caused by the value changing from undefined to a defined value, which should not happen.'
    );
  } else if (
    !isControlled &&
    wasControlled.current &&
    !didWarnOnControlledToUncontrolled.current
  ) {
    didWarnOnControlledToUncontrolled.current = true;
    wasControlled.current = isControlled;
    console.error(
      'A component is changing from controlled to uncontrolled. This may be caused by the value changing from a defined value to undefined, which should not happen.'
    );
  }

  return [
    (isControlled ? controlledValue : internalValue)!,
    useEvent((value: T) => {
      if (isControlled) {
        return onChange?.(value);
      } else {
        setInternalValue(value);
        return onChange?.(value);
      }
    }),
  ] as const;
}

/**
 * The useEvent function is an utility that ensures the stability of the
 * provided callback across renders.
 */
export const useEvent = function useEvent<
  F extends (...args: Array<ExplicitAny>) => ExplicitAny,
  P extends Array<ExplicitAny> = Parameters<F>,
  R = ReturnType<F>,
>(cb: (...args: P) => R) {
  const cache = useLatestValue(cb);
  return useCallback((...args: P) => cache.current(...args), [cache]);
};

/**
 * The useLatestValue function is an utility used to obtain the latest stable
 * value within your components.
 */
export function useLatestValue<T>(value: T) {
  const cache = useRef(value);

  useLayoutEffect(() => {
    cache.current = value;
  }, [value]);

  return cache;
}
